import React, { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { supabase } from '../utils/supabaseClient';
import { useUser } from './UserContext';

// profiles.user_id references users(id) — a DIFFERENT id space than auth.uid(). Resolve the
// app user's users.id (+ company_id, needed to create a profiles row) from the auth session.
// Reading/writing profiles keyed by auth.uid() matches nothing and silently no-ops.
const resolveBusinessUser = async () => {
  const { data: { user: authUser } } = await supabase.auth.getUser();
  if (!authUser) return null;
  const { data } = await supabase
    .from('users')
    .select('id, company_id')
    .eq('auth_user_id', authUser.id)
    .maybeSingle();
  return data || null; // { id, company_id } | null
};

const ThemeContext = createContext(null);

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) throw new Error('useTheme must be used within a ThemeProvider');
  return context;
};

export const ThemeProvider = ({ children }) => {
  // Initialize from localStorage, default to 'dark' for all users
  const [themeMode, setThemeMode] = useState(() => {
    const saved = localStorage.getItem('theme');
    // Default to 'dark' theme for better UX (will be overridden by database preference on auth)
    return saved || 'dark';
  });
  const [systemPrefersDark, setSystemPrefersDark] = useState(() =>
    typeof window !== 'undefined' && window.matchMedia('(prefers-color-scheme: dark)').matches
  );

  // Track if we've loaded from database to avoid saving the default
  const [isInitialized, setIsInitialized] = useState(false);

  // Board #2414: the result of saveThemeToDatabase used to be thrown away, and the Appearance
  // tab showed "Saved to database" on EVERY click because the only thing it awaited was a
  // synchronous React setter that cannot fail. Offline, the write never happened and the
  // choice was gone on reload — while the UI reported success. The real outcome now lives
  // here, where the write happens, and the tab renders it.
  const [themeSaveStatus, setThemeSaveStatus] = useState('idle'); // idle | saving | saved | error
  // The choice we could not persist, replayed when the connection comes back.
  const pendingThemeRef = useRef(null);
  // The theme the database already holds (or that we loaded with). The save effect used to fire
  // when isInitialized flipped true at app start, i.e. with no user signed in yet, so it failed
  // with no_user and Appearance opened on "Couldn't save". Only a CHANGE from this is saved.
  const persistedThemeRef = useRef(null);

  // Load the saved theme once, when the start-up sign-in check has finished (BEFORE applying
  // any changes). It used to read users + profiles itself through supabase.auth.getUser(),
  // which holds the auth lock for a network round trip, so every other request at start-up
  // queued behind it — and UserContext was reading the same profiles.preferences anyway
  // (page speed, 2026-10-25). Same rule as before: one load per page load; signed out, or the
  // profile could not be read, keeps the localStorage theme.
  const { user: signedInUser, loading: userLoading } = useUser();
  const themeLoadedRef = useRef(false);
  useEffect(() => {
    if (userLoading || themeLoadedRef.current) return;
    themeLoadedRef.current = true;

    const dbTheme = signedInUser?.preferences?.theme;
    if (dbTheme) {
      persistedThemeRef.current = dbTheme;
      setThemeMode(dbTheme);
      localStorage.setItem('theme', dbTheme);
    } else {
      persistedThemeRef.current = themeMode;
    }
    setIsInitialized(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userLoading]); // Runs once — themeMode here is the localStorage value we started with

  // Keep System mode synchronized when the OS preference changes while the app is open.
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleSystemThemeChange = (event) => {
      setSystemPrefersDark(event.matches);
      if (themeMode === 'system') {
        document.documentElement.classList.toggle('dark', event.matches);
      }
    };
    mediaQuery.addEventListener('change', handleSystemThemeChange);
    return () => mediaQuery.removeEventListener('change', handleSystemThemeChange);
  }, [themeMode]);

  // Apply theme to DOM whenever it changes
  useEffect(() => {
    const root = document.documentElement;

    if (themeMode === 'dark') {
      root.classList.add('dark');
    } else if (themeMode === 'light') {
      root.classList.remove('dark');
    } else if (themeMode === 'system') {
      // Follow system preference
      const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
      if (prefersDark) {
        root.classList.add('dark');
      } else {
        root.classList.remove('dark');
      }
    }

    // Save to localStorage
    localStorage.setItem('theme', themeMode);

    // Only save to database if initialized (user explicitly changed theme, not initial load)
    if (isInitialized && themeMode !== persistedThemeRef.current) {
      setThemeSaveStatus('saving');
      saveThemeToDatabase(themeMode).then((result) => {
        if (result?.success) {
          pendingThemeRef.current = null;
          persistedThemeRef.current = themeMode;
          setThemeSaveStatus('saved');
        } else if (result?.reason === 'no_user') {
          // Nobody signed in: localStorage already holds the choice and there is no account
          // to write it to. That is not a failed save.
          setThemeSaveStatus('idle');
        } else {
          pendingThemeRef.current = themeMode;
          setThemeSaveStatus('error');
        }
      });
    }
  }, [themeMode, isInitialized]);

  // Replay a theme the network refused. Without this the user is told to try again later and
  // then has to remember to; the choice is already known, so just send it.
  useEffect(() => {
    const flush = async () => {
      const pending = pendingThemeRef.current;
      if (!pending) return;
      setThemeSaveStatus('saving');
      const result = await saveThemeToDatabase(pending);
      if (result?.success) {
        pendingThemeRef.current = null;
        setThemeSaveStatus('saved');
      } else {
        setThemeSaveStatus('error');
      }
    };
    window.addEventListener('online', flush);
    return () => window.removeEventListener('online', flush);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Save theme to database with retry logic
  const saveThemeToDatabase = async (theme, retryCount = 0) => {
    const MAX_RETRIES = 3;

    // Offline, supabase.auth.getUser() resolves with user:null because its fetch failed, so
    // resolveBusinessUser returns null and the old code took the 'no_user' early return —
    // indistinguishable from "not signed in", never retried, never surfaced. Name the case.
    if (typeof navigator !== 'undefined' && navigator.onLine === false) {
      return { success: false, reason: 'offline' };
    }

    try {
      const businessUser = await resolveBusinessUser();
      if (!businessUser) {
        return { success: false, reason: 'no_user' };
      }

      // Merge into existing preferences (the row may not exist yet — upsert creates it)
      const { data: currentProfileData } = await supabase
        .from('profiles')
        .select('preferences')
        .eq('user_id', businessUser.id)
        .maybeSingle();

      const currentPrefs = currentProfileData?.preferences || {};
      const newPrefs = { ...currentPrefs, theme };

      // Upsert (keyed by users.id) so a user without a profiles row gets one created
      // instead of the old .update() silently no-op'ing on 0 matched rows.
      const { data: updateData, error } = await supabase
        .from('profiles')
        .upsert({
          user_id: businessUser.id,
          company_id: businessUser.company_id,
          preferences: newPrefs,
          updated_at: new Date().toISOString()
        }, { onConflict: 'user_id' })
        .select();

      const data = updateData && updateData.length > 0 ? updateData[0] : null;

      if (error) {
        // ✅ FIX: Suppress non-critical profile errors (406/PGRST116)
        if (error.code !== 'PGRST116') {
          console.error('Failed to save theme to database:', error);
        }

        // Retry on network errors or temporary failures
        if (retryCount < MAX_RETRIES && (error.code === 'PGRST301' || error.message.includes('network'))) {
          await new Promise(resolve => setTimeout(resolve, 1000 * (retryCount + 1))); // Exponential backoff
          return saveThemeToDatabase(theme, retryCount + 1);
        }

        return { success: false, error };
      }

      return { success: true, data };
    } catch (err) {
      console.error('Error saving theme:', err);

      // Retry on exceptions
      if (retryCount < MAX_RETRIES) {
        await new Promise(resolve => setTimeout(resolve, 1000 * (retryCount + 1)));
        return saveThemeToDatabase(theme, retryCount + 1);
      }

      return { success: false, error: err };
    }
  };

  const isDarkMode = useMemo(() => 
    themeMode === 'dark' ||
    (themeMode === 'system' && systemPrefersDark)
  , [themeMode, systemPrefersDark]);

  const value = useMemo(() => ({ themeMode, setThemeMode, isDarkMode, themeSaveStatus }), [themeMode, setThemeMode, isDarkMode, themeSaveStatus]);

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};
